(ns metabase.lib.schema.aggregation
  (:refer-clojure :exclude [some #?(:clj doseq)])
  (:require
   [metabase.lib.hierarchy :as lib.hierarchy]
   [metabase.lib.schema.expression :as expression]
   [metabase.lib.schema.mbql-clause :as mbql-clause]
   [metabase.util.i18n :as i18n]
   [metabase.util.malli.registry :as mr]
   [metabase.util.performance :refer [some #?(:clj doseq)]]))

;; count has an optional expression arg. This is the number of non-NULL values -- corresponds to count(<expr>) in SQL
(mbql-clause/define-catn-mbql-clause :count :- :type/Integer
  [:expression [:? [:schema [:ref ::expression/expression]]]])

;; cum-count has an optional expression arg
(mbql-clause/define-catn-mbql-clause :cum-count :- :type/Integer
  [:expression [:? [:schema [:ref ::expression/expression]]]])

(mbql-clause/define-tuple-mbql-clause :avg :- :type/Float
  [:schema [:ref ::expression/number]])

(mr/def ::distinct.arg
  [:and
   [:ref ::expression/expression]
   ;; you're not allowed to do distinct values of nil
   [:some
    {:error/message "You're not allowed to do distinct values of nil"}]])

;;; number of distinct values of something.
(mbql-clause/define-tuple-mbql-clause :distinct :- :type/Integer
  [:schema [:ref ::distinct.arg]])

(mbql-clause/define-tuple-mbql-clause :distinct-where :- :type/Integer
  [:schema [:ref ::distinct.arg]]
  [:schema [:ref ::expression/boolean]])

(mbql-clause/define-tuple-mbql-clause :count-where :- :type/Integer
  [:schema [:ref ::expression/boolean]])

;;; min and max should work on anything orderable, including numbers, temporal values, and even text values.
(mbql-clause/define-tuple-mbql-clause :max
  [:schema [:ref ::expression/orderable]])

;;; check either the type of the first arg, or fall back to `:type/Number` is the type cannot be determined.
(lib.hierarchy/derive ::type-is-type-of-first-arg-or-number :lib.type-of/type-is-type-of-first-arg)

(defmethod expression/type-of-method ::type-is-type-of-first-arg-or-number
  [expr]
  (let [expr-type ((get-method expression/type-of-method :lib.type-of/type-is-type-of-first-arg) expr)]
    (if (= expr-type ::expression/type.unknown)
      :type/Number
      expr-type)))

(lib.hierarchy/derive :max ::type-is-type-of-first-arg-or-number)

;;; apparently median and percentile only work for numeric args in Postgres, as opposed to anything orderable. Not
;;; sure this makes sense conceptually, but since there probably isn't as much of a use case we can keep that
;;; restriction in MBQL for now.
(mbql-clause/define-tuple-mbql-clause :median
  [:schema [:ref ::expression/number]])

(lib.hierarchy/derive :median ::type-is-type-of-first-arg-or-number)

(mbql-clause/define-tuple-mbql-clause :min
  [:schema [:ref ::expression/orderable]])

(lib.hierarchy/derive :min ::type-is-type-of-first-arg-or-number)

(mr/def ::percentile.percentile
  [:and
   {:error/message "valid percentile"}
   [:ref ::expression/number]
   [:fn
    {:error/message "percentile must be between zero and one"}
    #(<= 0 % 1)]])

(mbql-clause/define-tuple-mbql-clause :percentile
  #_expr       [:ref ::expression/number]
  #_percentile [:ref ::percentile.percentile])

(lib.hierarchy/derive :percentile ::type-is-type-of-first-arg-or-number)

(mbql-clause/define-tuple-mbql-clause :share :- :type/Float
  [:schema [:ref ::expression/boolean]])

(mbql-clause/define-tuple-mbql-clause :stddev :- :type/Float
  [:schema [:ref ::expression/number]])

(mbql-clause/define-tuple-mbql-clause :sum
  [:schema [:ref ::expression/number]])

(mbql-clause/define-tuple-mbql-clause :cum-sum
  [:schema [:ref ::expression/number]])

(lib.hierarchy/derive :sum ::type-is-type-of-first-arg-or-number)

(lib.hierarchy/derive :cum-sum ::type-is-type-of-first-arg-or-number)

(mbql-clause/define-tuple-mbql-clause :sum-where
  [:schema [:ref ::expression/number]]
  [:schema [:ref ::expression/boolean]])

(lib.hierarchy/derive :sum-where ::type-is-type-of-first-arg-or-number)

(mbql-clause/define-tuple-mbql-clause :var :- :type/Float
  #_expr [:schema [:ref ::expression/number]])

(doseq [tag [:aggregation
             :avg
             :count
             :cum-count
             :count-where
             :distinct
             :distinct-where
             :max
             :median
             :min
             :offset
             :percentile
             :share
             :stddev
             :sum
             :cum-sum
             :sum-where
             :var
             :metric
             :measure]]
  (lib.hierarchy/derive tag ::aggregation-clause-tag))

;; Window-function aggregations: clauses whose value at a given row depends on other rows in the result set (running
;; totals, offsets, etc.). Consumers that need to reason about "does this aggregation compose with GROUPING SETS / row
;; splitting / etc." should test for this hierarchy tag.
(doseq [tag [:cum-count
             :cum-sum
             :offset]]
  (lib.hierarchy/derive tag ::window-aggregation-clause-tag))

(defn- contains-clause-with-tag?
  "True if `x` is a clause whose tag derives from `hierarchy-tag`, or an expression that transitively contains one."
  [hierarchy-tag x]
  (letfn [(walk [x]
            (when-let [[tag _opts & args] (when (vector? x) x)]
              (or (lib.hierarchy/isa? tag hierarchy-tag)
                  ;; Case has shape [:case opts [[cond expr]...] default-expr?].
                  ;; `:if` is an alias for `:case`.
                  (if (#{:case :if} tag)
                    (let [[pairs fallback] args]
                      (or (some walk (eduction cat pairs))
                          (walk fallback)))
                    (some walk args)))))]
    (walk x)))

(defn- aggregation-expression?
  "A clause is a valid aggregation if it is an aggregation clause, or it is an expression that transitively contains
  a single aggregation clause."
  [x]
  (contains-clause-with-tag? ::aggregation-clause-tag x))

(defn window-aggregation-expression?
  "True if `x` is a window-function aggregation clause, or an expression that transitively contains one."
  [x]
  (contains-clause-with-tag? ::window-aggregation-clause-tag x))

(mr/def ::aggregation
  [:and
   [:ref :metabase.lib.schema.mbql-clause/clause]
   [:fn
    {:error/message #(i18n/tru "Aggregations should contain at least one aggregation function.")
     :error/friendly true}
    aggregation-expression?]])

(mr/def ::aggregations
  [:sequential {:min 1} [:ref ::aggregation]])

(defn- has-unaggregated-ref?
  "Checks if `x` contains a column reference that is not wrapped in an aggregation
  function, e.g. the `[:field 2]` in `[:+ [:sum [:field 1]] [:field 2]]`."
  [x]
  (when (vector? x)
    (let [[tag _opts & args] x]
      (cond
        (#{:field :expression} tag)
        true

        (lib.hierarchy/isa? tag ::aggregation-clause-tag)
        false

        ;; case/if conditions can reference unaggregated refs, we
        ;; only need to check the result branches are aggregated
        (#{:case :if} tag)
        (let [[pairs default] args]
          (boolean (or (some (fn [[_cond expr]] (has-unaggregated-ref? expr)) pairs)
                       (has-unaggregated-ref? default))))

        :else
        (boolean (some has-unaggregated-ref? args))))))

(mr/def ::aggregation-with-no-unaggregated-refs
  "Stricter variant of ::aggregation used for query builder verification. Not enforced
  on whole queries, so saved questions with unaggregated fields keep working."
  [:and
   [:ref ::aggregation]
   [:fn
    {:error/message #(i18n/tru "Fields in custom aggregations must be wrapped with a function like Sum.")
     :error/friendly true}
    (complement has-unaggregated-ref?)]])

(def aggregation-operators
  "The list of available aggregation operator.
  The order of operators is relevant for the front end."
  [{:short            :count
    :requires-column? false
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Count of rows")
                         :column-name  (i18n/tru "Count")
                         :description  (i18n/tru "Total number of rows in the answer.")})}
   {:short            :sum
    :supported-field  :metabase.lib.types.constants/summable
    :requires-column? true
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Sum of ...")
                         :column-name  (i18n/tru "Sum")
                         :description  (i18n/tru "Sum of all the values of a column.")})}
   {:short            :avg
    :supported-field  :metabase.lib.types.constants/summable
    :requires-column? true
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Average of ...")
                         :column-name  (i18n/tru "Average")
                         :description  (i18n/tru "Average of all the values of a column")})}
   {:short            :median
    :supported-field  :metabase.lib.types.constants/summable
    :requires-column? true
    :driver-feature   :percentile-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Median of ...")
                         :column-name  (i18n/tru "Median")
                         :description  (i18n/tru "Median of all the values of a column")})}
   {:short            :distinct
    :supported-field  :any
    :requires-column? true
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Number of distinct values of ...")
                         :column-name  (i18n/tru "Distinct values")
                         :description  (i18n/tru "Number of unique values of a column among all the rows in the answer.")})}
   {:short            :cum-sum
    :supported-field  :metabase.lib.types.constants/summable
    :requires-column? true
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Cumulative sum of ...")
                         :column-name  (i18n/tru "Sum")
                         :description  (i18n/tru "Additive sum of all the values of a column.\ne.x. total revenue over time.")})}
   {:short            :cum-count
    :requires-column? false
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Cumulative count of rows")
                         :column-name  (i18n/tru "Count")
                         :description  (i18n/tru "Additive count of the number of rows.\ne.x. total number of sales over time.")})}
   {:short            :stddev
    :supported-field  :metabase.lib.types.constants/summable
    :requires-column? true
    :driver-feature   :standard-deviation-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Standard deviation of ...")
                         :column-name  (i18n/tru "SD")
                         :description  (i18n/tru "Number which expresses how much the values of a column vary among all rows in the answer.")})}
   {:short            :min
    :supported-field  :metabase.lib.types.constants/scope
    :requires-column? true
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Minimum of ...")
                         :column-name  (i18n/tru "Min")
                         :description  (i18n/tru "Minimum value of a column")})}
   {:short            :max
    :supported-field  :metabase.lib.types.constants/scope
    :requires-column? true
    :driver-feature   :basic-aggregations
    :display-info     (fn []
                        {:display-name (i18n/tru "Maximum of ...")
                         :column-name  (i18n/tru "Max")
                         :description  (i18n/tru "Maximum value of a column")})}])

(mr/def ::operator
  [:map {:closed true}
   [:lib/type [:= :operator/aggregation]]
   [:short (into [:enum] (map :short) aggregation-operators)]
   [:supported-field {:optional true} [:maybe :keyword]] ; TODO more precise type?
   [:requires-column? :boolean]
   [:driver-feature :keyword]           ; TODO more precise type?
   [:display-info fn?]])
